import React, { useState, useEffect, useCallback } from 'react';
import { 
  Eye, Users, RefreshCw, Globe, Clock, Smartphone, Monitor, 
  ArrowUpRight, ArrowDownRight, Minus, MapPin,
  Bot, ExternalLink, Play, Mail, Briefcase, Sparkles
} from 'lucide-react';

// Date formatting helpers
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.getDate();
  const suffix = getDaySuffix(day);
  return date.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  }).replace(/\d+/, day + suffix);
};

const getDaySuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatHour = (hour24) => {
  let pstHour = (hour24 - 8 + 24) % 24;
  const ampm = pstHour >= 12 ? 'PM' : 'AM';
  const hour12 = pstHour % 12 || 12;
  return `${hour12}:00 ${ampm} PST`;
};

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [liveVisitors, setLiveVisitors] = useState({ count: 0, visitors: [] });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${backendUrl}/api/admin/analytics?days=${dateRange}`;
      if (dateRange === 'year') {
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const daysSinceJan1 = Math.floor((now - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
        url = `${backendUrl}/api/admin/analytics?days=${daysSinceJan1}`;
      } else if (dateRange === 'all') {
        url = `${backendUrl}/api/admin/analytics?days=3650`;
      } else if (dateRange === 'custom' && customStart && customEnd) {
        url = `${backendUrl}/api/admin/analytics?start_date=${customStart}&end_date=${customEnd}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, dateRange, customStart, customEnd]);

  const fetchLiveVisitors = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/live-visitors`, {
        credentials: 'include'
      });
      const data = await response.json();
      setLiveVisitors(data);
    } catch (error) {
      console.error('Error fetching live visitors:', error);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchLiveVisitors();
    const interval = setInterval(fetchLiveVisitors, 30000);
    return () => clearInterval(interval);
  }, [fetchLiveVisitors]);

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
  };

  const MomentumBadge = ({ value }) => {
    const isPositive = value > 0;
    const isNeutral = value === 0;
    return (
      <div className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
        isNeutral ? 'bg-stone-100 text-stone-500' :
        isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {isNeutral ? <Minus className="w-3 h-3" /> :
         isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {Math.abs(value)}%
      </div>
    );
  };

  if (loading && !analytics) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  // Device breakdown
  const devices = analytics?.device_breakdown || {};
  const totalDevices = (devices.mobile || 0) + (devices.desktop || 0) + (devices.tablet || 0);
  const mobilePercent = totalDevices > 0 ? Math.round((devices.mobile || 0) / totalDevices * 100) : 0;
  const desktopPercent = totalDevices > 0 ? Math.round((devices.desktop || 0) / totalDevices * 100) : 0;

  // AI Traffic
  const aiTraffic = analytics?.ai_traffic || { total: 0, by_source: [], bot_crawls: 0 };

  // Extract CTA clicks from categories
  const clicksByCategory = analytics?.clicks_by_category || {};
  
  // Work With Me clicks
  const workWithMeClicks = (clicksByCategory.opportunities || [])
    .filter(c => c.name?.toLowerCase().includes('work') || c.url?.includes('airtable'))
    .reduce((sum, c) => sum + c.count, 0);

  // Data Dreaming clicks
  const dataDreamingSiteClicks = (clicksByCategory.resources || [])
    .filter(c => c.url?.includes('datadreaming.cloud') && !c.url?.includes('beehiiv'))
    .reduce((sum, c) => sum + c.count, 0);
  
  const newsletterClicks = (clicksByCategory.resources || [])
    .filter(c => c.url?.includes('beehiiv') || c.name?.toLowerCase().includes('newsletter'))
    .reduce((sum, c) => sum + c.count, 0);

  // SIP Sessions (YouTube)
  const sipSessionsClicks = (clicksByCategory.brand || [])
    .filter(c => c.url?.includes('youtube') || c.url?.includes('youtu.be'))
    .reduce((sum, c) => sum + c.count, 0);

  // Social clicks (no Twitter)
  const socialClicks = {
    instagram: (clicksByCategory.brand || []).filter(c => c.url?.includes('instagram')).reduce((sum, c) => sum + c.count, 0),
    linkedin: (clicksByCategory.brand || []).filter(c => c.url?.includes('linkedin')).reduce((sum, c) => sum + c.count, 0),
    youtube: sipSessionsClicks,
    email: (clicksByCategory.brand || []).filter(c => c.url?.includes('mailto:')).reduce((sum, c) => sum + c.count, 0),
  };

  // Date range label
  const getDateRangeLabel = () => {
    switch(dateRange) {
      case '1': return 'Last 24 Hours';
      case '7': return 'Last 7 Days';
      case '30': return 'Last 30 Days';
      case 'year': return `${new Date().getFullYear()}`;
      case 'all': return 'All Time';
      case 'custom': return customStart && customEnd ? `${formatDate(customStart)} → ${formatDate(customEnd)}` : 'Custom Range';
      default: return '';
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">Signal Dashboard</h2>
          <p className="text-sm text-stone-500">{getDateRangeLabel()}</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-stone-100 rounded-lg p-1">
            {[
              { value: '1', label: '24h' },
              { value: '7', label: '7d' },
              { value: '30', label: '30d' },
              { value: 'year', label: '2026' },
              { value: 'all', label: 'All' },
              { value: 'custom', label: 'Custom' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleDateRangeChange(option.value)}
                data-testid={`date-range-${option.value}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  dateRange === option.value
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => { fetchAnalytics(); fetchLiveVisitors(); }}
            className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-lg"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Custom Date Range Picker */}
      {dateRange === 'custom' && (
        <div className="flex flex-wrap items-center gap-3 p-4 bg-stone-50 rounded-xl border border-stone-200">
          <span className="text-sm text-stone-600">From:</span>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          <span className="text-sm text-stone-600">To:</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          <button
            onClick={fetchAnalytics}
            disabled={!customStart || !customEnd}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      )}

      {/* Quick Summary Bar */}
      <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-stone-700">Quick Stats:</span>
          <span className="bg-white px-3 py-1 rounded-full border border-stone-200">
            <span className="text-teal-600 font-semibold">{workWithMeClicks}</span> Work With Me
          </span>
          <span className="bg-white px-3 py-1 rounded-full border border-stone-200">
            <span className="text-purple-600 font-semibold">{dataDreamingSiteClicks}</span> Data Dreaming
          </span>
          <span className="bg-white px-3 py-1 rounded-full border border-stone-200">
            <span className="text-pink-600 font-semibold">{newsletterClicks}</span> Newsletter
          </span>
          <span className="text-stone-400">|</span>
          {socialClicks.instagram > 0 && (
            <span className="text-stone-600">📸 {socialClicks.instagram}</span>
          )}
          {socialClicks.linkedin > 0 && (
            <span className="text-stone-600">💼 {socialClicks.linkedin}</span>
          )}
          {socialClicks.youtube > 0 && (
            <span className="text-stone-600">▶️ {socialClicks.youtube}</span>
          )}
          {socialClicks.email > 0 && (
            <span className="text-stone-600">✉️ {socialClicks.email}</span>
          )}
        </div>
      </div>

      {/* Live + Core Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Live Visitors */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
              <div className="absolute inset-0 w-2.5 h-2.5 bg-white rounded-full animate-ping"></div>
            </div>
            <span className="text-xs opacity-90">Live</span>
          </div>
          <p className="text-3xl font-bold mt-1" data-testid="live-visitors-count">{liveVisitors.count}</p>
        </div>

        {/* Views */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-stone-500">
              <Eye className="w-4 h-4" />
              <span className="text-xs font-medium">Views</span>
            </div>
            <MomentumBadge value={analytics?.momentum?.views_change || 0} />
          </div>
          <p className="text-2xl font-bold text-stone-900" data-testid="total-views">
            {analytics?.total_views?.toLocaleString() || 0}
          </p>
        </div>
        
        {/* Visitors */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-stone-500">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Visitors</span>
            </div>
            <MomentumBadge value={analytics?.momentum?.visitors_change || 0} />
          </div>
          <p className="text-2xl font-bold text-stone-900" data-testid="unique-visitors">
            {analytics?.unique_visitors?.toLocaleString() || 0}
          </p>
        </div>

        {/* Peak Time */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
          <div className="flex items-center gap-2 text-stone-500 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">Peak Time</span>
          </div>
          <p className="text-lg font-bold text-stone-900">
            {analytics?.peak_times?.day?.slice(0, 3) || 'N/A'}
          </p>
          <p className="text-xs text-stone-500">{analytics?.peak_times?.hour !== undefined ? formatHour(analytics.peak_times.hour) : '--'}</p>
        </div>

        {/* AI Traffic */}
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 text-white" data-testid="ai-traffic-summary">
          <div className="flex items-center gap-2 mb-1">
            <Bot className="w-4 h-4" />
            <span className="text-xs opacity-90">AI Discovered</span>
          </div>
          <p className="text-2xl font-bold">{aiTraffic.total}</p>
          <p className="text-xs opacity-75">{aiTraffic.bot_crawls} bot crawls</p>
        </div>
      </div>

      {/* CTA Performance */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Work With Me */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Briefcase className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-900">Work With Me</h3>
              <p className="text-xs text-stone-400">Business inquiries</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-teal-600" data-testid="work-with-me-clicks">{workWithMeClicks}</p>
          <p className="text-sm text-stone-500 mt-1">form clicks</p>
        </div>

        {/* Data Dreaming */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-900">Data Dreaming</h3>
              <p className="text-xs text-stone-400">Community engagement</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> Visit site
              </span>
              <span className="text-xl font-bold text-purple-600" data-testid="data-dreaming-site-clicks">{dataDreamingSiteClicks}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Newsletter clicks
              </span>
              <span className="text-xl font-bold text-pink-600" data-testid="newsletter-clicks">{newsletterClicks}</span>
            </div>
          </div>
        </div>

        {/* SIP Sessions */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <Play className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-stone-900">SIP Sessions</h3>
              <p className="text-xs text-stone-400">YouTube video clicks</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-red-600" data-testid="sip-sessions-clicks">{sipSessionsClicks}</p>
          <p className="text-sm text-stone-500 mt-1">video clicks</p>
        </div>
      </div>

      {/* Social Links Performance (no Twitter) */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
        <h3 className="font-semibold text-stone-900 mb-4">Social Links</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg">
            <span className="text-2xl">📸</span>
            <p className="text-2xl font-bold text-stone-900 mt-2">{socialClicks.instagram}</p>
            <p className="text-xs text-stone-500">Instagram</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
            <span className="text-2xl">💼</span>
            <p className="text-2xl font-bold text-stone-900 mt-2">{socialClicks.linkedin}</p>
            <p className="text-xs text-stone-500">LinkedIn</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg">
            <span className="text-2xl">▶️</span>
            <p className="text-2xl font-bold text-stone-900 mt-2">{socialClicks.youtube}</p>
            <p className="text-xs text-stone-500">YouTube</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-stone-50 to-stone-100 rounded-lg">
            <span className="text-2xl">✉️</span>
            <p className="text-2xl font-bold text-stone-900 mt-2">{socialClicks.email}</p>
            <p className="text-xs text-stone-500">Email</p>
          </div>
        </div>
      </div>

      {/* Traffic Sources + Device + Locations */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Traffic Sources */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-stone-400" />
            Traffic Sources
          </h3>
          {analytics?.top_referrers?.length > 0 ? (
            <div className="space-y-2">
              {analytics.top_referrers.slice(0, 6).map((ref, index) => (
                <div key={index} className="flex items-center justify-between p-2 hover:bg-stone-50 rounded-lg">
                  <span className="flex items-center gap-2 text-sm text-stone-700">
                    <span>{ref.icon}</span>
                    {ref.source}
                  </span>
                  <span className="text-sm font-medium text-stone-500">{ref.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-400 text-sm text-center py-4">No traffic data yet</p>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <h3 className="font-semibold text-stone-900 mb-4">Device Split</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="flex items-center gap-1.5 text-stone-600">
                  <Smartphone className="w-4 h-4" /> Mobile
                </span>
                <span className="font-medium">{mobilePercent}%</span>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 rounded-full" style={{ width: `${mobilePercent}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="flex items-center gap-1.5 text-stone-600">
                  <Monitor className="w-4 h-4" /> Desktop
                </span>
                <span className="font-medium">{desktopPercent}%</span>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${desktopPercent}%` }} />
              </div>
            </div>
          </div>
          <p className="text-xs text-stone-400 mt-4">{totalDevices} total sessions</p>
        </div>

        {/* Locations */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-stone-400" />
            Top Locations
          </h3>
          {analytics?.locations?.top_visitors?.length > 0 ? (
            <div className="space-y-2">
              {analytics.locations.top_visitors.slice(0, 6).map((loc, i) => (
                <div key={i} className="flex items-center justify-between p-2 hover:bg-stone-50 rounded-lg">
                  <span className="text-sm text-stone-700">{loc.location}</span>
                  <span className="text-sm font-medium text-stone-500">{loc.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-400 text-sm text-center py-4">No location data yet</p>
          )}
        </div>
      </div>

      {/* AI/GEO Traffic Section */}
      {(aiTraffic.total > 0 || aiTraffic.bot_crawls > 0) && (
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-100" data-testid="ai-traffic-section">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Bot className="w-5 h-5 text-violet-600" />
            AI Traffic Details
            <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full ml-2">GEO Tracking</span>
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* AI Platform Referrals */}
            <div>
              <p className="text-sm text-stone-600 mb-3 font-medium">Discovered via AI Platforms</p>
              {aiTraffic.by_source?.length > 0 ? (
                <div className="space-y-2">
                  {aiTraffic.by_source.map((source, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                      <span className="flex items-center gap-2 text-sm font-medium text-stone-700">
                        <span className="text-lg">{source.icon}</span>
                        {source.source}
                      </span>
                      <span className="text-sm font-bold text-violet-700">{source.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-stone-400 text-sm py-4 text-center bg-white rounded-lg">No AI referrals detected yet</p>
              )}
            </div>

            {/* AI Bot Info */}
            <div>
              <p className="text-sm text-stone-600 mb-3 font-medium">What this means</p>
              <div className="bg-white rounded-lg p-4 shadow-sm text-sm text-stone-600 space-y-2">
                <p>• <strong>AI Referrals:</strong> Visitors who clicked a link from ChatGPT, Claude, Perplexity, etc.</p>
                <p>• <strong>Bot Crawls ({aiTraffic.bot_crawls}):</strong> AI bots indexing your site for their knowledge base</p>
                <p className="text-xs text-stone-400 mt-3">Note: We can only detect traffic source, not what was said about you in AI conversations.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
